import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import {
  AlbumCellProps,
  FolderBreadcrumbText,
  LoadingSpinner,
} from '../../components/Molecules';
import { ClubSelectFolderPopup } from '../../components/Popups';
import { ClubTemplate } from '../../components/Templates/Clubs';
import { SpacePrivacyPolicy, SpaceType } from '../../constants/space';
import { AppToastInfoOption } from '../../constants/toast';
import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { TranslationLocale } from '../../i18n';
import { WebBoosterType } from '../../models/UserStore';
import { useRecordStore } from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useSystemStore } from '../../stores/SystemStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';

const { fetchSpace, seeSpace } = useSpaceStore.getState();
const { fetchRegions } = useSystemStore.getState();

interface State {
  selectedBoosterType: WebBoosterType | undefined;
  partitionId: string;
  isLoading: boolean;
  isFinishedFetchSpace: boolean;
  showAppBarTitle: boolean;
}

const ClubScreen: React.FC = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { showPopup, hidePopup } = usePopup();
  const { classroomId: classroomId } = useParams();

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const partitionId = useSessionStore((store) => store.partitionId);
  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  const [state, setState] = useState<State>({
    selectedBoosterType: userAccount?.settings.webBoosterType,
    partitionId: `${userAccount?.settings.webBoosterType}-${FolderBreadcrumbText.All}`,
    isLoading: true,
    isFinishedFetchSpace: false,
    showAppBarTitle: false,
  });

  useEffect(() => {
    setState((s) => ({
      ...s,
      partitionId:
        partitionId ??
        `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`,
    }));
  }, [partitionId, state.selectedBoosterType]);

  useRedirectEntry();

  // MARK: Get Space
  const spaceListFieldName = `${classroomId}`;
  useSpaceStoreListField('spaces', spaceListFieldName);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceListFieldName, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchSpace: true }));
        });
    }
  }, [classroomId, spaceListFieldName]);

  const { classroom, classroomMembership } = useSpaceStore((store) => {
    const space = store.getSpace(classroomId ?? '');
    const membership = store.getActiveUserMembership(classroomId ?? '');
    return {
      classroom: space,
      classroomMembership: membership,
    };
  }, shallow);

  const isAllowToAccessClub =
    (classroom?.privacyPolicy === SpacePrivacyPolicy.Public ||
      (classroom?.privacyPolicy === SpacePrivacyPolicy.PrivateAndVisible &&
        classroomMembership?.isInGroup)) ??
    false;

  const isOtherUserHomeClub =
    classroom?.type === SpaceType.Home && classroomId !== activeUserId;

  const breadcrumbs = useMemo(() => {
    const folders =
      classroom?.getAllPartitions(userAccount?.settings.webBoosterType) ?? [];
    const selectFolder = folders.find(
      (folder) => folder.partitionId === state.partitionId,
    );
    if (selectFolder) {
      return [selectFolder];
    }
    return [];
  }, [userAccount?.settings.webBoosterType, classroom, state.partitionId]);

  useRedirectHome(
    (!isAllowToAccessClub || isOtherUserHomeClub) && state.isFinishedFetchSpace,
  );

  useEffect(() => {
    if (classroomId) {
      seeSpace(classroomId).catch(console.error);
    }
  }, [classroomId]);

  // MARK: Club Recent Albums
  const onClickAlbum = useCallback(
    (gameSetId: string) => {
      navigate(`/club/${classroomId}/journey/${gameSetId}`, {
        state: { withBackState: true },
      });
    },
    [navigate, classroomId],
  );

  const recentAlbumFieldName = `${classroomId}_recent`;
  const recentAlbumField = useSpaceStoreListField(
    'albums',
    recentAlbumFieldName,
  );

  const fetchRecentAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchRecentAlbums(
        {
          fieldName: recentAlbumFieldName,
          classroomId: classroomId ?? '',
        },
        cursor,
      ),
    [classroomId, recentAlbumFieldName],
  );
  const { refresh: refreshClubRecentAlbums } = useFetchList(
    recentAlbumField,
    fetchRecentAlbums,
  );

  useEffect(() => {
    if (classroomId) {
      refreshClubRecentAlbums().catch(console.error);
    }
  }, [classroomId, refreshClubRecentAlbums]);

  const recentAlbums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(recentAlbumFieldName);
  }, arrayEqual(shallow));

  // MARK: Club Normal Albums
  const albumNormalFieldName = `${classroomId}_${state.selectedBoosterType}_${state.partitionId}`;
  const albumNormalField = useSpaceStoreListField(
    'albums',
    albumNormalFieldName,
  );

  const fetchAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchAlbums(
        {
          type: albumNormalFieldName,
          classroomId: classroomId ?? '',
          // webBoosterType: state.selectedBoosterType,
          partitionId:
            state.partitionId ===
            `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
              ? undefined
              : state.partitionId,
          withAlbumSummaries: true,
        },
        cursor,
      ),
    [
      albumNormalFieldName,
      classroomId,
      state.partitionId,
      state.selectedBoosterType,
    ],
  );

  const {
    fetchMore: fetchMoreAlbums,
    refresh: refreshAlbums,
    hasCursor,
  } = useFetchList(albumNormalField, fetchAlbums);

  useEffect(() => {
    if (classroomId) {
      refreshAlbums()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isLoading: false }));
        });
    }
  }, [refreshAlbums, classroomId]);

  const loadMoreItems = useCallback(() => {
    fetchMoreAlbums().catch(console.error);
  }, [fetchMoreAlbums]);

  const albums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(albumNormalFieldName);
  }, arrayEqual(shallow));

  const albumSummaries = useRecordStore((store) => {
    const gameSetIds = albums.map((item) => item.gameSetId);

    return gameSetIds.map((gameSetId) =>
      store.getAlbumSummary(gameSetId, activeUserId ?? ''),
    );
  }, arrayEqual(shallow));

  const albumData = useMemo<AlbumCellProps[]>(() => {
    return albums.map((album) => {
      const isNoGame = album.statistics.gameCount === 0;
      const albumSummary = albumSummaries.find(
        (item) => item?.gameSetId === album.gameSetId,
      );
      return {
        album: album,
        albumClassroom: classroom,
        albumSummary: albumSummary,
        onClick: () => {
          isNoGame
            ? toast(t('Content is about to be unlocked.'), AppToastInfoOption)
            : onClickAlbum(album.gameSetId);
        },
      };
    });
  }, [albums, albumSummaries, classroom, t, onClickAlbum]);

  window.addEventListener('scroll', () => {
    if (window.scrollY > (state.showAppBarTitle ? 140 : 190)) {
      setState((s) => ({ ...s, showAppBarTitle: true }));
    } else {
      setState((s) => ({ ...s, showAppBarTitle: false }));
    }
  });

  useEffect(() => {
    fetchRegions({
      locale: getUiLocale(i18n.language as TranslationLocale),
    }).catch(console.error);
  }, [i18n.language]);

  const regionData = useSystemStore((store) => store.getRegions());

  const onClickAIRevision = useCallback(() => {
    navigate(`/club/${classroomId}/memory-boost`);
    window.scrollTo(0, 0);
  }, [navigate, classroomId]);

  const onClickRecentAlbum = useCallback(() => {
    navigate(`/club/${classroomId}/journey-recent`);
  }, [classroomId, navigate]);

  const onClickBreadcrumb = useCallback(
    (folder: string) => {
      if (classroom) {
        showPopup(
          <ClubSelectFolderPopup
            webBoosterType={userAccount?.settings.webBoosterType}
            space={classroom}
            selectedFolderId={folder}
            folders={classroom.getAllPartitions(
              userAccount?.settings.webBoosterType,
            )}
            newFolder={undefined}
            onClickSelect={onClickBreadcrumb}
            onCancel={hidePopup}
            onConfirm={() => {
              useSessionStore.getState().setPartitionId(folder);
              hidePopup();
            }}
          />,
        );
      }
    },
    [userAccount?.settings.webBoosterType, classroom, hidePopup, showPopup],
  );

  return !state.isFinishedFetchSpace ? (
    <LoadingSpinner loading={true} />
  ) : userProfile && classroom ? (
    <ClubTemplate
      space={classroom}
      webBoosterType={state.selectedBoosterType}
      breadcrumbs={breadcrumbs}
      hasRecentAlbum={recentAlbums.length > 0}
      albumData={albumData}
      showAppBarTitle={state.showAppBarTitle}
      translatedRegions={regionData}
      isLoading={state.isLoading}
      hasCursor={hasCursor}
      onClickAIRevision={onClickAIRevision}
      onClickRecentAlbum={onClickRecentAlbum}
      onClickBreadcrumb={() => {
        onClickBreadcrumb(state.partitionId);
      }}
      loadMoreItems={loadMoreItems}
    />
  ) : null;
};

export default ClubScreen;
